import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, MapPin, Phone } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { publicApi } from "@/lib/api";
import { useSite } from "@/hooks/useSite";
import { trackEvent } from "@/lib/analytics";

const SERVICIOS = [
  "Diagnóstico de brechas",
  "Implementación de sistemas de gestión",
  "Auditoría interna",
  "Preparación para certificación",
  "Capacitación",
  "Gestión de riesgos",
  "Diseño de procesos y documentación",
  "ISO 9001",
  "ISO 14001",
  "ISO/IEC 27001",
  "FSC Cadena de Custodia",
  "INTE G38",
  "Bandera Azul Ecológica",
  "Otro servicio",
];

const PAISES = [
  { code: "+506", name: "Costa Rica" },
  { code: "+507", name: "Panamá" },
  { code: "+503", name: "El Salvador" },
  { code: "+502", name: "Guatemala" },
  { code: "+504", name: "Honduras" },
  { code: "+505", name: "Nicaragua" },
  { code: "+1", name: "USA/Canadá" },
  { code: "+52", name: "México" },
  { code: "+57", name: "Colombia" },
  { code: "+34", name: "España" },
  { code: "+51", name: "Perú" },
  { code: "+56", name: "Chile" },
  { code: "+54", name: "Argentina" },
];

const INITIAL = {
  nombre: "", telefono: "", codigo_pais: "+506", email: "", empresa: "",
  pais: "Costa Rica", ciudad: "", servicio_interes: "", mensaje: "",
  preferencia_contacto: "email", acepta_politica: false, honeypot: "",
};

export default function Contact() {
  const site = useSite();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Requerido";
    if (!form.telefono.trim()) e.telefono = "Requerido";
    if (!form.email.trim()) e.email = "Requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Correo no válido";
    if (!form.empresa.trim()) e.empresa = "Requerido";
    if (!form.pais.trim()) e.pais = "Requerido";
    if (!form.ciudad.trim()) e.ciudad = "Requerido";
    if (!form.acepta_politica) e.acepta_politica = "Debe aceptar la política";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt.preventDefault();
    if (!validate()) { toast.error("Por favor completa los campos requeridos."); return; }

    // If prefers WhatsApp, build deep-link
    if (form.preferencia_contacto === "whatsapp") {
      const wa = (site?.whatsapp || "").replace(/[^\d]/g, "");
      const msg = `Hola, mi nombre es ${form.nombre} de ${form.empresa}. Me interesa recibir información sobre ${form.servicio_interes || "sus servicios"}. Mi necesidad es: ${form.mensaje || "-"}.`;
      if (wa) {
        trackEvent("whatsapp_click", { source: "contact_form", service: form.servicio_interes || "sin_especificar" });
        window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
      }
    }

    setLoading(true);
    try {
      await publicApi.contact({ ...form });
      trackEvent("generate_lead", {
        contact_method: form.preferencia_contacto,
        service: form.servicio_interes || "sin_especificar",
        country: form.pais,
      });
      toast.success("Solicitud recibida. Nos pondremos en contacto en breve.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos enviar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="relative scroll-mt-[72px] py-24 md:py-36 border-t border-[#1A1A1A]/10 bg-[#F9F6F0]" data-testid="contact-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {/* Left column: big type + direct info */}
          <Reveal className="col-span-12 md:col-span-5 lg:col-span-4">
            <div className="eyebrow mb-6">— 11 · Contacto</div>
            <h2 className="display-2 leading-[0.95]">Conversemos.</h2>
            <p className="mt-8 text-[#4A4A4A] max-w-sm leading-relaxed">
              Contáctenos y juntos construiremos la mejor solución para su necesidad.
            </p>

            <div className="mt-14 space-y-6 text-sm">
              <a href={`mailto:${site?.email}`} onClick={() => trackEvent("email_click", { source: "contact_section" })} className="flex items-start gap-4 group" data-testid="contact-email-link">
                <Mail className="w-4 h-4 mt-1 text-[#1B3B2B]" strokeWidth={1.5} />
                <div>
                  <div className="eyebrow text-[#1A1A1A]/50">Correo</div>
                  <div className="mt-1 group-hover:text-[#1B3B2B] transition-colors">{site?.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MessageSquare className="w-4 h-4 mt-1 text-[#1B3B2B]" strokeWidth={1.5} />
                <div>
                  <div className="eyebrow text-[#1A1A1A]/50">WhatsApp</div>
                  <div className="mt-1">{site?.whatsapp_display}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-4 h-4 mt-1 text-[#1B3B2B]" strokeWidth={1.5} />
                <div>
                  <div className="eyebrow text-[#1A1A1A]/50">Teléfono</div>
                  <div className="mt-1">{site?.telefono}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-4 h-4 mt-1 text-[#1B3B2B]" strokeWidth={1.5} />
                <div>
                  <div className="eyebrow text-[#1A1A1A]/50">Horario · Modalidad</div>
                  <div className="mt-1">{site?.horario}</div>
                  <div className="text-[#4A4A4A]">{site?.modalidad}</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right column: form */}
          <Reveal delay={0.15} className="col-span-12 md:col-span-7 lg:col-span-8">
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="contact-form">
              <Field label="Nombre completo *" name="nombre" value={form.nombre} onChange={(v) => set("nombre", v)} error={errors.nombre} />

              <div>
                <label className="eyebrow block mb-2">Teléfono *</label>
                <div className="flex gap-2">
                  <select
                    value={form.codigo_pais}
                    onChange={(e) => set("codigo_pais", e.target.value)}
                    className="w-24 bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm"
                    data-testid="contact-codigo-pais"
                  >
                    {PAISES.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
                  </select>
                  <input
                    type="tel"
                    value={form.telefono}
                    onChange={(e) => set("telefono", e.target.value)}
                    className="flex-1 bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm"
                    data-testid="contact-telefono"
                  />
                </div>
                {errors.telefono && <div className="text-xs text-[#8B1E1E] mt-1">{errors.telefono}</div>}
              </div>

              <Field label="Correo electrónico *" name="email" type="email" value={form.email} onChange={(v) => set("email", v)} error={errors.email} />
              <Field label="Empresa *" name="empresa" value={form.empresa} onChange={(v) => set("empresa", v)} error={errors.empresa} />
              <Field label="País *" name="pais" value={form.pais} onChange={(v) => set("pais", v)} error={errors.pais} />
              <Field label="Ciudad *" name="ciudad" value={form.ciudad} onChange={(v) => set("ciudad", v)} error={errors.ciudad} />

              <div className="md:col-span-2">
                <label className="eyebrow block mb-2">Servicio de interés</label>
                <select
                  value={form.servicio_interes}
                  onChange={(e) => set("servicio_interes", e.target.value)}
                  className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm"
                  data-testid="contact-servicio"
                >
                  <option value="">Seleccionar servicio</option>
                  {SERVICIOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="eyebrow block mb-2">Cuéntanos cómo podemos ayudarte</label>
                <textarea
                  value={form.mensaje}
                  onChange={(e) => set("mensaje", e.target.value)}
                  rows={3}
                  className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm resize-none"
                  data-testid="contact-mensaje"
                />
              </div>

              <div className="md:col-span-2">
                <label className="eyebrow block mb-3">¿Cómo prefieres continuar la conversación?</label>
                <div className="flex flex-wrap gap-3">
                  <RadioPill checked={form.preferencia_contacto === "email"} onClick={() => set("preferencia_contacto", "email")} testId="pref-email">
                    Recibir respuesta por correo
                  </RadioPill>
                  <RadioPill checked={form.preferencia_contacto === "whatsapp"} onClick={() => set("preferencia_contacto", "whatsapp")} testId="pref-whatsapp">
                    Continuar por WhatsApp
                  </RadioPill>
                </div>
              </div>

              {/* honeypot */}
              <input type="text" name="honeypot" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

              <div className="md:col-span-2 flex items-start gap-3 mt-2">
                <input
                  id="acepta"
                  type="checkbox"
                  checked={form.acepta_politica}
                  onChange={(e) => set("acepta_politica", e.target.checked)}
                  className="mt-1"
                  data-testid="contact-acepta"
                />
                <label htmlFor="acepta" className="text-xs text-[#4A4A4A] leading-relaxed">
                  He leído y acepto la <a href="/politica-privacidad" className="underline">política de privacidad</a> y autorizo el tratamiento de mis datos personales para atender esta solicitud.
                </label>
              </div>
              {errors.acepta_politica && <div className="md:col-span-2 text-xs text-[#8B1E1E]">{errors.acepta_politica}</div>}

              <div className="md:col-span-2 pt-4">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={loading}
                  className="kb-btn"
                  data-testid="contact-submit"
                >
                  {loading ? "Enviando..." : "Enviar solicitud"}
                  <Send className="w-3.5 h-3.5" />
                </motion.button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, error }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm"
        data-testid={`contact-${name}`}
      />
      {error && <div className="text-xs text-[#8B1E1E] mt-1">{error}</div>}
    </div>
  );
}

function RadioPill({ checked, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm border transition-colors ${checked ? "bg-[#1B3B2B] text-[#F9F6F0] border-[#1B3B2B]" : "bg-transparent text-[#1A1A1A] border-[#1A1A1A]/25 hover:border-[#1A1A1A]"}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
